// SPDX-License-Identifier: GPL-3.0-or-later
// Code-built kicker (hole or saucer). The upstream kicker loads JSON meshes. This one is a dark
// disc, a white edge ring, and for a cup a short wall. The origin is the centre on the playfield.

import { type BufferGeometry, CircleGeometry, CylinderGeometry, RingGeometry } from "three";
import type { KickerSpec } from "../../shared/contracts.ts";
import { INSERT_LIFT } from "../coords.ts";

/** The parts of a kicker. A part is null when the type has none. */
export interface KickerGeometry {
  /** The dark disc. */
  readonly hole: BufferGeometry;
  /** The edge ring. */
  readonly rim: BufferGeometry;
  /** The open wall of a cup. Null for a hole. */
  readonly wall: BufferGeometry | null;
}

/** Type 0 is invisible. */
export function isKickerVisible(spec: KickerSpec): boolean {
  return (spec.kickerType ?? 0) !== 0;
}

/** Build the geometry of a kicker. */
export function generateKicker(spec: KickerSpec): KickerGeometry {
  const type = spec.kickerType ?? 0;
  const r = spec.radius;
  const hole = new CircleGeometry(r * 0.95, 32);
  hole.rotateX(-Math.PI / 2);
  hole.translate(0, INSERT_LIFT, 0);
  hole.name = `kicker.hole-${spec.name}`;

  const rim = new RingGeometry(r * 0.95, r * 1.12, 32);
  rim.rotateX(-Math.PI / 2);
  rim.translate(0, INSERT_LIFT + 0.05, 0);
  rim.name = `kicker.rim-${spec.name}`;

  let wall: BufferGeometry | null = null;
  if (type === 2 || type === 6) {
    const wallHeight = type === 6 ? 7 : 4;
    wall = new CylinderGeometry(r * 1.05, r * 1.05, wallHeight, 32, 1, true);
    wall.translate(0, wallHeight / 2, 0);
    wall.name = `kicker.wall-${spec.name}`;
  }
  return { hole, rim, wall };
}
